import React, { useEffect, useState } from 'react';
import apiClient from '../lib/api-client';

import Plot from 'react-plotly.js';
import { useTheme } from '../ThemeContext';
import { useWindowWidth } from '../lib/useWindowWidth';

const OUTAGE_METRICS = ['min', 'mean', 'max'];

const METRIC_LABELS = {
  min: 'Min',
  mean: 'Mean',
  max: 'Max',
  weather_severity: 'Weather Severity'
};

const METRIC_COLORS = {
  min: '#1f77b4',
  mean: '#ff7f0e',
  max: '#2ca02c',
  weather_severity: '#d62728'
};

const METRIC_ICONS = {
  min: 'fa-arrow-down',
  mean: 'fa-equals',
  max: 'fa-arrow-up'
};

const WeeklyMapVisual = ({
  currentRegion,
  setCurrentRegion,
  predictionData,
  onCountyClick,
  stateId
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;

  // Internal region state for chart/table
  const [internalRegion, setInternalRegion] = useState(currentRegion);

  // Sync internalRegion when parent selects a new county
  useEffect(() => {
    setInternalRegion(currentRegion);
  }, [currentRegion]);

  const [chartData, setChartData] = useState(null);
  const [isTableView, setIsTableView] = useState(false);
  const [mobileMetric, setMobileMetric] = useState('mean');
  const [CustomercountiesMap, setCustomerCountiesMap] = useState({});
  const [isCountyDataLoaded, setIsCountyDataLoaded] = useState(false);
  const [countiesList, setCountiesList] = useState([]);

  useEffect(() => {
    const fetchCounties = async () => {
      try {
        const cList = await apiClient.listCounties();
        setCountiesList(cList);
        // Create a mapping from FIPS code to customer count
        const countyMapping = {};
        cList.forEach(county => {
          countyMapping[county.fips] = county.customers;
        });

        setCustomerCountiesMap(countyMapping);
        setIsCountyDataLoaded(true);
      } catch (error) {
        console.error('Error fetching counties:', error);
      }
    };

    fetchCounties();
  }, []);

  useEffect(() => {
    if (predictionData && isCountyDataLoaded) {
      if (!internalRegion) {
        // Aggregate state view
        const aggregatedData = aggregateCountyData();
        setChartData(aggregatedData);
      } else {
        // Find county id correctly, internalRegion could be object or string
        const geoid = typeof internalRegion === 'object' ? internalRegion.id : internalRegion;
        if (geoid && predictionData[geoid]) {
          const dailyData = predictionData[geoid];
          const dates = dailyData.map(d => d.date.split('T')[0]);

          const trace = ['min', 'mean', 'max', 'weather_severity'].map(metric => ({
            x: dates,
            y: dailyData.map(d => d.customers[metric]),
            name: metric === "weather_severity" ? "Weather Severity" : `Customers ${metric}`,
            type: 'scatter',
            mode: 'lines+markers',
            yaxis: metric === "weather_severity" ? 'y2' : 'y'
          }));

          setChartData(trace);
        }
      }
    }
  }, [predictionData, internalRegion, CustomercountiesMap, isCountyDataLoaded]);

  const aggregateCountyData = () => {
    if (!predictionData || Object.keys(CustomercountiesMap).length === 0) return null;

    const allDates = new Set();

    Object.values(predictionData).forEach(countyArray => {
      countyArray.forEach(entry => {
        if (entry?.date) allDates.add(entry.date.split('T')[0]);
      });
    });

    const sortedDates = Array.from(allDates).sort();

    const aggregatedData = sortedDates.map(date => {
      const countiesForDate = [];

      Object.entries(predictionData).forEach(([countyId, countyArray]) => {
        const entry = countyArray.find(d => d.date.split('T')[0] === date);
        if (entry && entry.customers) {
          countiesForDate.push({
            ...entry,
            countyId: countyId, // Keep as string to match CustomercountiesMap keys
          });
        }
      });

      const totalCustomers = countiesForDate.reduce((sum, d) => {
        return sum + (CustomercountiesMap[d.countyId] || 0);
      }, 0);

      const weightedSeverity = totalCustomers > 0
        ? countiesForDate.reduce((sum, d) => {
          const countyCustomers = CustomercountiesMap[d.countyId] || 0;
          const weight = countyCustomers / totalCustomers;
          return sum + (d.customers.weather_severity * weight);
        }, 0)
        : 0;

      return {
        date,
        min: countiesForDate.reduce((sum, d) => sum + (d.customers.min || 0), 0),
        max: countiesForDate.reduce((sum, d) => sum + (d.customers.max || 0), 0),
        mean: countiesForDate.reduce((sum, d) => sum + (d.customers.mean || 0), 0),
        weather_severity: weightedSeverity
      };
    });

    return [
      {
        x: sortedDates,
        y: aggregatedData.map(d => d.min),
        name: 'Min Across Counties',
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#2196F3' }
      },
      {
        x: sortedDates,
        y: aggregatedData.map(d => d.mean),
        name: 'Mean Across Counties',
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#4CAF50' }
      },
      {
        x: sortedDates,
        y: aggregatedData.map(d => d.max),
        name: 'Max Across Counties',
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#F44336' }
      },
      {
        x: sortedDates,
        y: aggregatedData.map(d => d.weather_severity),
        name: 'Weather Severity',
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#b08b27ff' },
        yaxis: 'y2'
      }
    ];
  };

  const getSeverityBucket = (value) => {
    if (value <= 0.3) return 'Low';
    if (value <= 0.5) return 'Medium';
    return 'High';
  };

  const DataTable = ({ data }) => {
    const rows = data[0]?.x.map((date, index) => ({
      date,
      ...data.reduce((acc, series) => ({
        ...acc,
        [series.name]: series.y[index]
      }), {})
    }));

    return (
      <div className={`table-responsive ${isDarkMode ? '' : 'table-light'} text-center`} style={{ maxHeight: '500px', overflowY: 'auto' }}>
        <table className={`table table-hover ${isDarkMode ? 'table-dark' : ''}`}>
          <thead className={`sticky-top ${isDarkMode ? 'bg-dark text-light' : 'bg-white'}`}>
            <tr>
              <th>Date</th>
              {data.map(series => (
                <th key={series.name}>{series.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.date}>
                <td>{row.date}</td>
                {data.map(series => (
                  <td key={series.name}>
                    {series.name === 'Weather Severity'
                      ? getSeverityBucket(row[series.name])
                      : (row[series.name]).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const getSelectedCountyName = () => {
    if (!internalRegion || !countiesList.length) return null;
    const geoid = typeof internalRegion === 'object' ? internalRegion.id : internalRegion;
    const county = countiesList.find(c => String(c.fips) === String(geoid));
    return county ? county.name : geoid;
  };

  const selectedCountyName = getSelectedCountyName();
  const getTraceMetric = (series) => {
    if (series?.meta?.metric) return series.meta.metric;

    const name = String(series?.name || '').toLowerCase();
    if (name.includes('weather')) return 'weather_severity';
    if (name.includes('min')) return 'min';
    if (name.includes('max')) return 'max';
    return 'mean';
  };

  const mobileOutageTrace = chartData?.find(series => getTraceMetric(series) === mobileMetric);
  const mobileSeverityTrace = chartData?.find(series => getTraceMetric(series) === 'weather_severity');
  const mobileChartData = [
    ...(mobileOutageTrace ? [{
      ...mobileOutageTrace,
      name: METRIC_LABELS[mobileMetric],
      yaxis: 'y',
      hovertemplate: '%{x}<br>Outages: %{y:,.0f}<extra></extra>',
      line: { color: METRIC_COLORS[mobileMetric], width: 3 },
      marker: { color: METRIC_COLORS[mobileMetric], size: 6 }
    }] : []),
    ...(mobileSeverityTrace ? [{
      ...mobileSeverityTrace,
      name: 'Weather Severity',
      yaxis: 'y2',
      hovertemplate: '%{x}<br>Severity: %{y:.2f}<extra></extra>',
      line: { color: METRIC_COLORS.weather_severity, width: 2 },
      marker: { color: METRIC_COLORS.weather_severity, size: 5 }
    }] : [])
  ];
  const sharedPlotColors = {
    plot_bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
    paper_bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
    font: {
      color: isDarkMode ? '#e2e8f0' : '#1f2937'
    }
  };
  const sharedAxisColor = isDarkMode ? '#cbd5e1' : '#475569';
  const sharedGridColor = isDarkMode ? '#334155' : '#f1f5f9';

  return (
    <div className="container-fluid mb-5">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3 weekly-map-toolbar">
            <h5 className="fw-bold text-main m-0">
              {internalRegion ? `Weekly Overview: ${selectedCountyName || 'Selected County'}` : `State Weekly Overview`}
            </h5>
            <div className="d-flex gap-2 weekly-map-actions">
              {internalRegion && (
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setInternalRegion(null)}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to State Summary
                </button>
              )}
              <button
                className="btn btn-outline-primary"
                onClick={() => setIsTableView(!isTableView)}
              >
                <i className={`fas fa-${isTableView ? 'chart-line' : 'table'} me-2`}></i>
                {isTableView ? 'Show Graph' : 'Show Table'}
              </button>
            </div>
          </div>

          <div className={`card shadow-sm ${isDarkMode ? 'bg-dark border-secondary text-light' : 'border-secondary'} w-100`}>
            <div className="card-body p-0" style={{ minHeight: isTableView ? 'auto' : (isMobile ? 300 : 500), overflowX: 'hidden' }}>
              {!isCountyDataLoaded && (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '300px' }}>
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="text-muted small">Loading customer data...</span>
                </div>
              )}
              {chartData && (
                <>
                  {isTableView ? (
                    <DataTable data={chartData} />
                  ) : isMobile ? (
                    <div className="weekly-mobile-chart">
                      <div className="weekly-mobile-metric-toggle px-3 pt-3">
                        <div className="btn-group w-100" role="group" aria-label="Outage metric">
                          {OUTAGE_METRICS.map(metric => (
                            <button
                              key={metric}
                              type="button"
                              className={`btn ${mobileMetric === metric ? 'btn-secondary active' : 'btn-outline-secondary'}`}
                              onClick={() => setMobileMetric(metric)}
                            >
                              <i className={`fas ${METRIC_ICONS[metric]} me-1`}></i>
                              {METRIC_LABELS[metric]}
                            </button>
                          ))}
                        </div>
                      </div>

                      <Plot
                        data={mobileChartData}
                        layout={{
                          ...sharedPlotColors,
                          xaxis: {
                            tickformat: '%b %-d',
                            nticks: 4,
                            tickfont: { size: 10, color: sharedAxisColor },
                            gridcolor: sharedGridColor,
                            automargin: true
                          },
                          yaxis: {
                            title: { text: 'Outages', font: { size: 11, color: isDarkMode ? '#e2e8f0' : '#1f2937' } },
                            tickfont: { size: 10, color: sharedAxisColor },
                            gridcolor: sharedGridColor,
                            automargin: true,
                            separatethousands: true,
                            rangemode: 'tozero'
                          },
                          yaxis2: {
                            title: { text: 'Severity', font: { size: 10, color: isDarkMode ? '#e2e8f0' : '#1f2937' } },
                            overlaying: 'y',
                            side: 'right',
                            range: [0, 1],
                            tickfont: { size: 9, color: sharedAxisColor },
                            showgrid: false,
                            zeroline: false,
                            automargin: true
                          },
                          height: 330,
                          autosize: true,
                          margin: { l: 52, r: 54, t: 42, b: 44 },
                          showlegend: true,
                          legend: {
                            orientation: 'h',
                            x: 0,
                            y: 1.16,
                            xanchor: 'left',
                            yanchor: 'bottom',
                            font: { size: 10, color: isDarkMode ? '#e2e8f0' : '#1f2937' }
                          }
                        }}
                        style={{ width: '100%' }}
                        useResizeHandler={true}
                        config={{ responsive: true, displayModeBar: false, topojsonURL: process.env.PUBLIC_URL + '/' }}
                      />
                    </div>
                  ) : (
                    <Plot
                      data={chartData}
                      layout={{
                        plot_bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                        paper_bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                        font: {
                          color: isDarkMode ? '#e2e8f0' : '#1f2937'
                        },
                        xaxis: {
                          title: 'Date',
                          side: 'bottom',
                          titlefont: { size: isMobile ? 11 : 14, color: isDarkMode ? '#e2e8f0' : '#1f2937' },
                          tickfont: { size: isMobile ? 10 : 12, color: isDarkMode ? '#cbd5e1' : '#475569' },
                          gridcolor: isDarkMode ? '#334155' : '#f1f5f9'
                        },
                        yaxis: {
                          title: { text: 'Outages', font: { size: isMobile ? 11 : 14, color: isDarkMode ? '#e2e8f0' : '#1f2937' } },
                          standoff: isMobile ? 8 : 25,
                          side: 'left',
                          titlefont: { size: isMobile ? 11 : 14, color: isDarkMode ? '#e2e8f0' : '#1f2937' },
                          tickfont: { size: isMobile ? 10 : 12, color: isDarkMode ? '#cbd5e1' : '#475569' },
                          gridcolor: isDarkMode ? '#334155' : '#f1f5f9'
                        },
                        yaxis2: {
                          title: { text: 'Weather Severity', font: { size: isMobile ? 11 : 14, color: isDarkMode ? '#e2e8f0' : '#1f2937' } },
                          standoff: isMobile ? 8 : 25,
                          side: 'right',
                          overlaying: 'y',
                          titlefont: { size: isMobile ? 11 : 14, color: isDarkMode ? '#e2e8f0' : '#1f2937' },
                          tickfont: { size: isMobile ? 10 : 12, color: isDarkMode ? '#cbd5e1' : '#475569' },
                          range: [0, 1],
                          showgrid: false,
                          zeroline: false
                        },
                        height: isMobile ? 300 : 500,
                        autosize: true,
                        margin: {
                          l: isMobile ? 48 : 100,
                          r: isMobile ? 42 : 100,
                          t: isMobile ? 24 : 40,
                          b: isMobile ? 64 : 80
                        },
                        showlegend: true,
                        legend: {
                          orientation: 'h',
                          yanchor: 'bottom',
                          y: isMobile ? -0.28 : -0.2,
                          xanchor: 'center',
                          x: 0.5,
                          font: { size: isMobile ? 10 : 12, color: isDarkMode ? '#e2e8f0' : '#1f2937' },
                          bgcolor: isDarkMode ? 'rgba(30,41,59,0.8)' : 'rgba(255,255,255,0.8)'
                        }
                      }}
                      style={{ width: '100%' }}
                      useResizeHandler={true}
                      config={{ responsive: true, displayModeBar: false, topojsonURL: process.env.PUBLIC_URL + '/' }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyMapVisual;
